// ForgotPassword.tsx — UNAUTHENTICATED reset flow (/password_reset.json).
// Two entry points:
//
//   1. ForgotPasswordModal — opened from the "Forgot password?" link on the
//      Login page. Step 1: username + email (+ Project ID for local users)
//      → POST ?action=request. Step 2: 6-digit code + new password
//      → POST ?action=confirm (typed-code form).
//
//   2. PasswordResetLanding — rendered when the URL matches /reset?token=…
//      (the clickable link in the email). Submits the TOKEN form, which is
//      self-contained — no username/project needed.
//
// Scope: a username is unique only WITHIN a scope. Global users send
// {username, email}; local (project-scoped) users must also send project_id,
// and the typed-code confirm REQUIRES project_id. The token form never needs
// it — the link carries the scope.
//
// ANTI-ENUMERATION (backend spec §9): the 404 is deliberately identical for
// unknown username / wrong email / unverified email / unknown project. We show
// the server's single combined message verbatim and NEVER tell the user which
// field was wrong.
//
// On a successful confirm the backend revokes ALL of the user's sessions — we
// send them back to the login screen, never treat them as signed in.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal as MModal, ModalBody, ModalContent } from '@heroui/react';

import {
  requestPasswordReset,
  confirmPasswordResetByToken,
  confirmPasswordResetByCode,
} from '@/features/logins/emailApi';

import Button from '@/components/Common/Button';
import Input from '@/components/Common/Input';
import FlexRow from '@/components/Common/FlexRow';
import DigitInput from '@/components/Common/DigitInput';

import { useResendCooldown } from '@/pages/Account/useCountdown';

const sixDigitRegex = /^\d{6}$/;
const emailRegex = /^\S+@\S+\.\S+$/;
const RESEND_COOLDOWN_SECONDS = 30;

const inputCn = "px-[16px] py-[12px] rounded-[8px] border-solid border-1 border-[--Mid-Grey] !bg-white";

// Server password policy (backend spec §6): 8–15 chars, upper + lower + digit
// + special. Evaluated live so each rule ticks as the user types.
const passwordRules = [
  { label: '8–15 characters',     test: (p: string) => p.length >= 8 && p.length <= 15 },
  { label: 'An uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter',  test: (p: string) => /[a-z]/.test(p) },
  { label: 'A digit',             test: (p: string) => /\d/.test(p) },
  { label: 'A special character', test: (p: string) => /[^A-Za-z0-9\s]/.test(p) },
];

const PasswordRules = ({ pwd }: { pwd: string }) => (
  <div className="border border-[--Mid-Grey] rounded-[4px] p-[10px] bg-[--Light-Grey]">
    <div className="text-[11px] font-bold text-[--Dark-Grey] mb-[4px] uppercase tracking-wider">
      Password must include
    </div>
    <div className="grid grid-cols-2 gap-x-[12px] gap-y-[2px]">
      { passwordRules.map((r) => {
        const ok = r.test(pwd);
        return (
          <div key={r.label} className="flex items-center gap-[6px] text-[12px]">
            <span className={`material-icons text-[14px] ${ok ? '!text-[--Primetime-main]' : '!text-[--Mid-Grey]'}`}>
              { ok ? 'check_circle' : 'radio_button_unchecked' }
            </span>
            <span className={ok ? 'text-black' : 'text-[--Dark-Grey]'}>{ r.label }</span>
          </div>
        );
      }) }
    </div>
  </div>
);

const passwordValid = (p: string) => passwordRules.every((r) => r.test(p));

const ErrorBanner = ({ msg }: { msg: string }) => (
  <div className="bg-[#FDECEA] border border-[--Error] rounded-[4px] p-[8px] flex items-start gap-[6px]">
    <span className="material-icons !text-[--Error] text-[16px]">error</span>
    <span className="text-[12px] text-[--Error] leading-[16px]">{ msg }</span>
  </div>
);

// ---------------------------------------------------------------------------
// Login-page modal: user clicks "Forgot password?"
// ---------------------------------------------------------------------------

interface ForgotPasswordModalProps {
  onClose:		() => void;
  // Local (project-scoped) users only — prefills + locks the reset to that
  // project. Omitted for global users. When set, the modal is reached from
  // the "Login to project" screen.
  projectId?:	string;
  // Prefill the username the user already typed on the login form.
  username?:	string;
}

export const ForgotPasswordModal = ({
  onClose, projectId: initialProjectId, username: initialUsername,
}: ForgotPasswordModalProps) => {
  const { t } = useTranslation();

  type Step = 'form' | 'code' | 'success';
  const [step, setStep] = React.useState<Step>('form');

  const [username, setUsername] = React.useState(initialUsername || '');
  const [email, setEmail] = React.useState('');
  const [projectId, setProjectId] = React.useState(initialProjectId || '');

  const [code, setCode] = React.useState('');
  const [newPwd, setNewPwd] = React.useState('');

  const [pending, setPending] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  // 429 throttle (5 requests / 15 min / IP). We honor Retry-After: submit stays
  // disabled and counts down rather than letting the user hammer the endpoint.
  const [lockSeconds, setLockSeconds] = React.useState(0);
  React.useEffect(() => {
    if (lockSeconds <= 0) return;
    const id = setTimeout(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [lockSeconds]);

  const emailInvalid = React.useMemo(
    () => email.length > 0 && !emailRegex.test(email),
    [email],
  );
  // Only complain once the user has LEFT the field. Keying the error off
  // "non-empty and not yet a valid address" lights it up on the first
  // character ("v" → invalid), which reads as an error while they're still
  // mid-way through typing. A submit-attempt flag wouldn't work here either:
  // the Send button is disabled while the address is invalid, so the user can
  // never trigger it.
  const [emailTouched, setEmailTouched] = React.useState(false);

  // Local users are scoped by project; the Project ID is required in that
  // context because the reset-code key is project-scoped.
  const isLocal = !!initialProjectId;
  const formIncomplete =
    !username.trim() || !email.trim() || emailInvalid || (isLocal && !projectId.trim());

  const submitRequest = React.useCallback(async () => {
    if (formIncomplete || lockSeconds > 0) return;
    setPending(true);
    setErrMsg('');
    try {
      const r = await requestPasswordReset(username, email, projectId);
      switch (r.kind) {
        case 'sent':
          cooldown.arm();
          setCode('');
          setStep('code');
          return;
        case 'no_match':
          // Combined 404 — show the server's single message verbatim. Do NOT
          // add copy hinting which field was wrong.
          setErrMsg(r.message);
          return;
        case 'rate_limited':
          setLockSeconds(r.retryAfter > 0 ? r.retryAfter : 900);
          setErrMsg(r.message);
          return;
        case 'unavailable':
        case 'failure':
          setErrMsg(r.message || t('email:errors:generic'));
          return;
      }
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [formIncomplete, lockSeconds, username, email, projectId, cooldown, t]);

  const submitConfirm = React.useCallback(async () => {
    if (!sixDigitRegex.test(code)) {
      setErrMsg(t('email:errors:bad_code_format'));
      return;
    }
    if (!passwordValid(newPwd)) {
      setErrMsg('New password does not meet the requirements below.');
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const r = await confirmPasswordResetByCode(email, code, newPwd, projectId);
      if (r.kind === 'ok') {
        // All sessions are revoked server-side — the user is NOT logged in.
        setStep('success');
        return;
      }
      if (r.kind === 'invalid_code') {
        // Expired / already used / unresolved — send them back to request a
        // fresh code rather than stranding them on a dead ticket.
        setCode('');
        setErrMsg(r.message);
        return;
      }
      setErrMsg(r.message || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [code, newPwd, email, projectId, t]);

  const canResend = cooldown.ready && !pending && lockSeconds === 0;

  return (
    <MModal backdrop="opaque"
            defaultOpen={true}
            hideCloseButton={true}
            scrollBehavior="outside"
            classNames={{
              backdrop: "bg-[#0000007f]",
              wrapper: "modalShadow",
              base: "!max-h-none rounded-[16px]",
              body: "!overflow-visible",
            }}
            onClose={onClose}
            radius="lg">
      <ModalContent>
        <ModalBody className="min-w-[440px] max-w-[440px] p-[32px] !overflow-visible">
          <FlexRow direction="column" className="gap-[16px] items-center">

            {/* Circular icon badge */}
            <FlexRow flex={0}
                     justify="center"
                     className="w-[80px] h-[80px] shrink-0 rounded-full bg-[#EDF1F9] items-center">
              <span className="material-icons-outlined !text-[--Primetime-main] text-[36px]">
                { step === 'success' ? 'check_circle' : 'vpn_key' }
              </span>
            </FlexRow>

            <h2 className="text-[24px] font-bold text-black m-0 text-center">
              { step === 'success' ? t('forgot:done_title') : t('forgot:title') }
            </h2>

            { step === 'success'
              ? <>
                  <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                    { t('forgot:done_message') }
                  </p>
                  <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
                    <Button caption={t('forgot:back_to_login')}
                            onClick={onClose} />
                  </FlexRow>
                </>
              : <>
                  <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                    { step === 'form' ? t('forgot:email_message') : t('forgot:code_message') }
                  </p>

                  {/* Step 2 has no username field — the confirm call is keyed by
                      email (+ project), not username. Show the account as read-only
                      context so the user can see WHO they're resetting and WHERE the
                      code was sent. */}
                  { step === 'code' &&
                    <div className="w-full bg-[#EDF1F9] rounded-[8px] py-[12px] px-[16px] text-center">
                      <span className="text-[15px] font-bold text-[--Primetime-main] break-all">
                        { email }
                      </span>
                      <div className="text-[12px] text-[--Dark-Grey] pt-[2px]">
                        { username }{ isLocal && projectId ? ` (${projectId})` : '' }
                      </div>
                    </div>
                  }

                  { step === 'form' &&
                    <>
                      <Input value={username}
                             label={t('login:username:caption')}
                             onValueChange={setUsername}
                             type="text"
                             autoFocus={!initialUsername}
                             autoComplete="off"
                             classNames={{ inputWrapper: inputCn }} />
                      { isLocal &&
                        <Input value={projectId}
                               label={t('login:project_id:caption')}
                               onValueChange={setProjectId}
                               type="text"
                               autoComplete="off"
                               classNames={{ inputWrapper: inputCn }} />
                      }
                      <Input value={email}
                             label={t('email:fields:email')}
                             onValueChange={setEmail}
                             onBlur={() => setEmailTouched(true)}
                             type="email"
                             autoFocus={!!initialUsername}
                             autoComplete="off"
                             isInvalid={emailTouched && emailInvalid}
                             errorMessage={t('email:errors:invalid_email')}
                             classNames={{ inputWrapper: inputCn }} />
                    </>
                  }

                  { step === 'code' &&
                    <>
                      <DigitInput value={code}
                                  onChange={setCode}
                                  disabled={pending}
                                  autoFocus />
                      <Input value={newPwd}
                             label={t('email:fields:new_password')}
                             onValueChange={setNewPwd}
                             type="password"
                             classNames={{ inputWrapper: inputCn }} />
                      <PasswordRules pwd={newPwd} />
                      <span onClick={() => { if (canResend) submitRequest(); }}
                            className={`text-[13px] ${
                              !cooldown.ready
                                ? 'text-[--Error] cursor-default'
                                : canResend
                                  ? 'text-[--Dark-Grey] cursor-pointer hover:underline'
                                  : 'text-[--Mid-Grey] cursor-default'
                            }`}>
                        { cooldown.ready
                            ? t('email:verify:resend')
                            : `${t('email:verify:resend')} ${cooldown.display}` }
                      </span>
                    </>
                  }

                  { errMsg !== '' && <ErrorBanner msg={errMsg} /> }

                  <FlexRow flex={0} justify="center" className="gap-[12px] w-full pt-[4px]">
                    <Button caption={t('modals:buttons:cancel')}
                            layout="white"
                            onClick={onClose} />
                    { step === 'form' &&
                      <Button caption={lockSeconds > 0
                                        ? `${t('forgot:send_reset')} (${lockSeconds}s)`
                                        : (pending ? '…' : t('forgot:send_reset'))}
                              isDisabled={pending || formIncomplete || lockSeconds > 0}
                              onClick={submitRequest} />
                    }
                    { step === 'code' &&
                      <Button caption={pending ? '…' : t('forgot:submit_reset')}
                              isDisabled={pending || code.length !== 6 || !passwordValid(newPwd)}
                              onClick={submitConfirm} />
                    }
                  </FlexRow>
                </>
            }
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </MModal>
  );
};

// ---------------------------------------------------------------------------
// /reset?token=... landing page (rendered by Login when the URL matches).
// Uses the TOKEN confirm form — self-contained, needs no username/project.
// ---------------------------------------------------------------------------

export const PasswordResetLanding = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const token = React.useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get('token') || '';
  }, [location.search]);

  const [newPwd, setNewPwd] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');
  const [done, setDone] = React.useState(false);

  const submit = React.useCallback(async () => {
    if (!token) {
      setErrMsg(t('forgot:bad_link'));
      return;
    }
    if (!passwordValid(newPwd)) {
      setErrMsg('New password does not meet the requirements below.');
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const r = await confirmPasswordResetByToken(token, newPwd);
      if (r.kind === 'ok') {
        // Every session is revoked server-side — route to login.
        setDone(true);
        return;
      }
      setErrMsg(r.message || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [token, newPwd, t]);

  const pageWrap = 'absolute right-0 left-0 top-0 bottom-0 bg-[--Light-Grey] flex items-center justify-center p-[24px]';
  const card = 'w-full max-w-[440px] bg-white loginBanner rounded-[16px] p-[32px]';

  // Circular icon badge — same treatment as the modal, so the emailed-link
  // landing doesn't read as a different product.
  const badge = (icon: string) => (
    <FlexRow flex={0}
             justify="center"
             className="w-[80px] h-[80px] rounded-full bg-[#EDF1F9] items-center">
      <span className="material-icons-outlined !text-[--Primetime-main] text-[36px]">{ icon }</span>
    </FlexRow>
  );

  if (done) {
    return (
      <div className={pageWrap}>
        <div className={card}>
          <FlexRow direction="column" className="gap-[16px] items-center">
            { badge('check_circle') }
            <h2 className="text-[24px] font-bold text-black m-0 text-center">
              { t('forgot:done_title') }
            </h2>
            <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
              { t('forgot:done_message') }
            </p>
            <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
              <Button caption={t('forgot:back_to_login')} onClick={() => navigate('/')} />
            </FlexRow>
          </FlexRow>
        </div>
      </div>
    );
  }

  return (
    <div className={pageWrap}>
      <div className={card}>
        <FlexRow direction="column" className="gap-[16px] items-center">
          { badge('vpn_key') }
          <h2 className="text-[24px] font-bold text-black m-0 text-center">
            { t('forgot:reset_title') }
          </h2>
          <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
            { token ? t('forgot:reset_subtitle') : t('forgot:bad_link') }
          </p>

          { token &&
            <>
              <Input value={newPwd}
                     label={t('email:fields:new_password')}
                     onValueChange={setNewPwd}
                     type="password"
                     autoFocus
                     classNames={{ inputWrapper: inputCn }} />
              <PasswordRules pwd={newPwd} />
              { errMsg !== '' && <ErrorBanner msg={errMsg} /> }
              <FlexRow flex={0} justify="center" className="gap-[12px] w-full pt-[4px]">
                <Button caption={t('forgot:back_to_login')}
                        layout="white"
                        onClick={() => navigate('/')} />
                <Button caption={pending ? '…' : t('forgot:submit_reset')}
                        isDisabled={pending || !passwordValid(newPwd)}
                        onClick={submit} />
              </FlexRow>
            </>
          }

          { !token &&
            <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
              <Button caption={t('forgot:back_to_login')} onClick={() => navigate('/')} />
            </FlexRow>
          }
        </FlexRow>
      </div>
    </div>
  );
};
